import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import MainLayout from '../../components/layout/MainLayout';
import { ScrollText, Loader2, Filter } from 'lucide-react';
import { auditApi } from '../../services/api';
import { toast } from 'sonner';

const actionColors = {
  user_created: 'bg-green-500/20 text-green-500',
  user_updated: 'bg-blue-500/20 text-blue-500',
  user_deleted: 'bg-red-500/20 text-red-500',
  role_changed: 'bg-purple-500/20 text-purple-500',
  status_changed: 'bg-orange-500/20 text-orange-500',
  permissions_updated: 'bg-cyan-500/20 text-cyan-500',
  login: 'bg-gray-500/20 text-gray-400',
  logout: 'bg-gray-500/20 text-gray-400',
  password_reset: 'bg-yellow-500/20 text-yellow-500',
};

const actionOptions = [
  'all', 'user_created', 'user_updated', 'user_deleted', 'role_changed',
  'status_changed', 'permissions_updated', 'login', 'logout',
];

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const AdminActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('all');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const qs = action !== 'all' ? `action=${action}&limit=100` : 'limit=100';
        const data = await auditApi.getLogs(qs);
        if (active) { setLogs(data.items); setTotal(data.total); }
      } catch (err) {
        toast.error(err.message || 'Failed to load activity log');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [action]);

  return (
    <MainLayout title="Activity Log" subtitle="Admin & account audit trail">
      <div data-testid="admin-activity-log">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2 text-gray-500">
            <ScrollText className="w-5 h-5" />
            <span className="text-sm">{total} recorded actions</span>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="px-4 py-2 rounded-xl glass-card bg-transparent outline-none capitalize"
              data-testid="activity-filter-action"
            >
              {actionOptions.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20" data-testid="activity-loading">
              <Loader2 className="w-7 h-7 animate-spin text-blue-500" />
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8B] text-white">
                  <th className="px-6 py-4 text-left">Time</th>
                  <th className="px-6 py-4 text-left">Actor</th>
                  <th className="px-6 py-4 text-left">Action</th>
                  <th className="px-6 py-4 text-left">Target</th>
                  <th className="px-6 py-4 text-left">IP</th>
                </tr>
              </thead>
              <tbody data-testid="activity-table-body">
                {logs.length === 0 ? (
                  <tr><td colSpan={5} className="px-6 py-12 text-center text-gray-500">No activity recorded.</td></tr>
                ) : logs.map((log, i) => (
                  <motion.tr
                    key={log.id}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                    className="border-b border-gray-500/10 hover:bg-blue-500/5"
                  >
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{fmt(log.timestamp)}</td>
                    <td className="px-6 py-4 text-sm">{log.actor_email || '—'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs rounded-full capitalize ${actionColors[log.action] || 'bg-gray-500/20 text-gray-500'}`}>
                        {(log.action || '').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{log.target_label || '—'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 font-mono">{log.ip_address || '—'}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          )}
        </motion.div>
      </div>
    </MainLayout>
  );
};

export default AdminActivityLog;
